import React, { useState, useEffect, useMemo } from "react";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import "./SWPCalculator.css";

const formatINR = (n) =>
  new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(
    Number(Math.round(n || 0))
  );

export default function SWPCalculator({ onBack }) {
  const [amount, setAmount] = useState(197765);
  const [withdrawal, setWithdrawal] = useState(6000);
  const [rate, setRate] = useState(8);
  const [years, setYears] = useState(1);
  const [results, setResults] = useState({
    invested: 197765,
    totalWithdrawn: 0,
    finalValue: 197765,
  });

  useEffect(() => {
    calculate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [amount, withdrawal, rate, years]);

  const calculate = () => {
    const monthlyRate = parseFloat(rate) / 100 / 12;
    const months = parseInt(years, 10) * 12;
    let balance = parseFloat(amount) || 0;
    let totalWithdrawn = 0;

    for (let i = 0; i < months; i++) {
      balance = balance * (1 + monthlyRate);
      const w = Math.min(balance, parseFloat(withdrawal) || 0);
      balance -= w;
      totalWithdrawn += w;
      if (balance <= 0) { balance = 0; break; }
    }

    setResults({
      invested: amount || 0,
      totalWithdrawn: totalWithdrawn || 0,
      finalValue: balance || 0,
    });
  };

  // Generate chart data
  const chartData = useMemo(() => {
    const monthlyRate = parseFloat(rate) / 100 / 12;
    const months = parseInt(years, 10) * 12;
    let balance = parseFloat(amount) || 0;
    let totalW = 0;
    const data = [{ name: 'Start', investment: balance, withdrawal: 0 }];

    const step = Math.max(1, Math.floor(months / 12));

    for (let i = 1; i <= months; i++) {
      balance = balance * (1 + monthlyRate);
      const w = Math.min(balance, parseFloat(withdrawal) || 0);
      balance -= w;
      totalW += w;
      if (balance <= 0) { balance = 0; }

      if (i % step === 0 || i === months) {
        const label = i < 12 ? `${i}M` : `${Math.round(i / 12 * 10) / 10}Y`;
        data.push({
          name: label,
          investment: Math.round(balance),
          withdrawal: Math.round(totalW),
        });
      }
    }
    return data;
  }, [amount, withdrawal, rate, years]);

  const rangeBg = (value, min, max) => {
    const pct = ((value - min) / (max - min)) * 100;
    return `linear-gradient(90deg, #7c3aed ${pct}%, rgba(255,255,255,0.06) ${pct}%)`;
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="swp-chart-tooltip">
          <p className="swp-tooltip-label">{label}</p>
          <p className="swp-tooltip-val" style={{ color: '#a855f7' }}>Balance: ₹ {formatINR(payload[0]?.value)}</p>
          <p className="swp-tooltip-val" style={{ color: '#3b82f6' }}>Withdrawn: ₹ {formatINR(payload[1]?.value)}</p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="swp-page">
      <div className="swp-inner">
        {/* Back + Title */}
        <button className="swp-back-btn" onClick={onBack}>← Back to Dashboard</button>

        <h1 className="swp-title">
          SWP <span className="swp-gradient-text">Calculator</span>
        </h1>
        <p className="swp-desc">
          An SWP calculator helps you estimate the returns and periodic withdrawals from your investment.<br />
          Plan your financial future with precision and make smarter decisions.
        </p>

        <div className="swp-grid">
          {/* LEFT — Input Card */}
          <div className="swp-card swp-left">
            <h3 className="swp-card-heading">
              <span className="swp-card-icon">📊</span> Your Investment Details
            </h3>

            <label className="swp-field-label">Total Investment <span className="swp-info-icon">ⓘ</span></label>
            <div className="swp-amount-box">
              <span className="swp-rupee">₹</span>
              <input
                type="text"
                className="swp-amount-input"
                value={formatINR(amount)}
                onChange={(e) => {
                  const raw = e.target.value.replace(/[^0-9]/g, '');
                  setAmount(raw === '' ? '' : Number(raw));
                }}
              />
            </div>

            <div className="swp-control">
              <label className="swp-control-label">
                Monthly Withdrawal (SWP Amount) <span className="swp-info-icon">ⓘ</span>
                <span className="swp-control-value">₹ {formatINR(withdrawal)}</span>
              </label>
              <input
                type="range" min="500" max="5000000" step="500"
                value={withdrawal}
                onChange={(e) => setWithdrawal(Number(e.target.value))}
                style={{ background: rangeBg(withdrawal, 500, 5000000) }}
              />
              <div className="swp-minmax"><span>₹ 500</span><span>₹ 50,00,000</span></div>
            </div>

            <div className="swp-control">
              <label className="swp-control-label">
                Expected Rate of Return (P.A.) <span className="swp-info-icon">ⓘ</span>
                <span className="swp-control-value">{rate}%</span>
              </label>
              <input
                type="range" min="1" max="30" step="0.5"
                value={rate}
                onChange={(e) => setRate(Number(e.target.value))}
                style={{ background: rangeBg(rate, 1, 30) }}
              />
              <div className="swp-minmax"><span>1%</span><span>30%</span></div>
            </div>

            <div className="swp-control">
              <label className="swp-control-label">
                Select Duration <span className="swp-info-icon">ⓘ</span>
                <span className="swp-control-value">{years} Yr</span>
              </label>
              <input
                type="range" min="1" max="30" step="1"
                value={years}
                onChange={(e) => setYears(Number(e.target.value))}
                style={{ background: rangeBg(years, 1, 30) }}
              />
              <div className="swp-minmax"><span>1 Yr</span><span>30 Yr</span></div>
            </div>

            <div className="swp-disclaimer">
              <span className="swp-disclaimer-icon">ⓘ</span>
              <p>All calculations are based on the assumptions you provide. Results are indicative and may vary with market performance.</p>
            </div>
          </div>

          {/* RIGHT — Results Card */}
          <div className="swp-card swp-right">
            <h3 className="swp-card-heading">
              <span className="swp-card-icon">📈</span> Your SWP Plan Summary
            </h3>

            {/* Summary Row */}
            <div className="swp-summary-row">
              <div className="swp-summary-item">
                <span className="swp-summary-label">Invested Amount</span>
                <span className="swp-summary-value">₹ {formatINR(results.invested)}</span>
              </div>
              <div className="swp-summary-item">
                <span className="swp-summary-label">Total Withdrawal</span>
                <span className="swp-summary-value swp-val-blue">₹ {formatINR(results.totalWithdrawn)}</span>
              </div>
              <div className="swp-summary-item">
                <span className="swp-summary-label">Final Value</span>
                <span className="swp-summary-value swp-val-green">₹ {formatINR(results.finalValue)}</span>
              </div>
            </div>

            {/* Chart */}
            <div className="swp-chart-section">
              <div className="swp-chart-header">
                <h4 className="swp-chart-title">Investment Projection <span className="swp-info-icon">ⓘ</span></h4>
                <span className="swp-chart-badge">Projected for {years} Year{years > 1 ? 's' : ''}</span>
              </div>

              <div className="swp-chart-wrap">
                <ResponsiveContainer width="100%" height={220}>
                  <AreaChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                    <defs>
                      <linearGradient id="swpGrad1" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#a855f7" stopOpacity={0.3} />
                        <stop offset="100%" stopColor="#a855f7" stopOpacity={0} />
                      </linearGradient>
                      <linearGradient id="swpGrad2" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#3b82f6" stopOpacity={0.2} />
                        <stop offset="100%" stopColor="#3b82f6" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" />
                    <XAxis dataKey="name" tick={{ fill: 'rgba(255,255,255,0.35)', fontSize: 11 }} axisLine={{ stroke: 'rgba(255,255,255,0.06)' }} tickLine={false} />
                    <YAxis tick={{ fill: 'rgba(255,255,255,0.35)', fontSize: 11 }} axisLine={{ stroke: 'rgba(255,255,255,0.06)' }} tickLine={false} tickFormatter={(v) => v >= 100000 ? `₹ ${(v/100000).toFixed(1)}L` : `₹ ${formatINR(v)}`} width={65} />
                    <Tooltip content={<CustomTooltip />} />
                    <Area type="monotone" dataKey="investment" stroke="#a855f7" strokeWidth={2} fill="url(#swpGrad1)" />
                    <Area type="monotone" dataKey="withdrawal" stroke="#3b82f6" strokeWidth={2} fill="url(#swpGrad2)" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>

              {/* Final Value Callout */}
              <div className="swp-final-callout">
                <span className="swp-final-label">Final Value</span>
                <span className="swp-final-amount">₹ {formatINR(results.finalValue)}</span>
              </div>

              {/* Legend */}
              <div className="swp-chart-legend">
                <span className="swp-legend-item"><span className="swp-legend-dot" style={{ background: '#a855f7' }} /> Investment Growth</span>
                <span className="swp-legend-item"><span className="swp-legend-dot" style={{ background: '#3b82f6' }} /> Total Withdrawal</span>
              </div>
            </div>

            <div className="swp-disclaimer">
              <span className="swp-disclaimer-icon">ⓘ</span>
              <p>SWP returns are not guaranteed. Actual returns may vary based on market conditions and fund performance.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
